import type { JsonObject, JsonValue } from "@/lib/api/types/json";

/** Rough mainland India bounds for map fallback and validation. */
export const INDIA_BOUNDS = {
  south: 6.4,
  west: 68.1,
  north: 35.5,
  east: 97.4,
} as const;

export type FleetHealth = "healthy" | "degraded" | "offline" | "unknown";

const HEALTH_SLA_FRESH_MS = 15 * 60 * 1000;
const HEALTH_SLA_STALE_MS = 60 * 60 * 1000;

function jsonNum(v: JsonValue | undefined): number | null {
  if (typeof v === "number" && Number.isFinite(v)) {
    return v;
  }
  if (typeof v === "string") {
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

function pickCoord(meta: JsonObject | null | undefined, keys: string[]): number | null {
  if (!meta) {
    return null;
  }
  for (const k of keys) {
    const v = meta[k];
    const n = jsonNum(v);
    if (n !== null) {
      return n;
    }
  }
  const install = meta.installation;
  if (install && typeof install === "object" && !Array.isArray(install)) {
    const o = install as JsonObject;
    for (const k of keys) {
      const n = jsonNum(o[k]);
      if (n !== null) {
        return n;
      }
    }
  }
  return null;
}

/** Lat/lng from device metadata, or null if unset. */
export function readDeviceCoordinates(meta: JsonObject | null | undefined): { lat: number; lng: number } | null {
  const lat = pickCoord(meta, ["latitude", "lat", "installation_latitude"]);
  const lng = pickCoord(meta, ["longitude", "lng", "lon", "installation_longitude"]);
  if (lat === null || lng === null) {
    return null;
  }
  if (lat < -90 || lat > 90 || lng < -180 || lng > 180) {
    return null;
  }
  return { lat, lng };
}

/** Clamp coordinates to mainland India bounds (fleet map shows India only). */
export function clampLatLngToIndia(lat: number, lng: number): { lat: number; lng: number } {
  return {
    lat: Math.min(INDIA_BOUNDS.north, Math.max(INDIA_BOUNDS.south, lat)),
    lng: Math.min(INDIA_BOUNDS.east, Math.max(INDIA_BOUNDS.west, lng)),
  };
}

function pickMetaString(meta: JsonObject | null | undefined, keys: string[]): string | null {
  if (!meta) {
    return null;
  }
  for (const k of keys) {
    const v = meta[k];
    if (typeof v === "string" && v.trim()) {
      return v.trim();
    }
  }
  return null;
}

/** Customer / end-user name from device metadata. */
export function readCustomerName(meta: JsonObject | null | undefined): string | null {
  return pickMetaString(meta, [
    "customer_name",
    "customerName",
    "client_name",
    "end_customer",
    "company_name",
    "customer",
    "client",
  ]);
}

/** Purchase / install / issue date from metadata (ISO or epoch). */
export function readDateOfIssue(meta: JsonObject | null | undefined): Date | null {
  if (!meta) {
    return null;
  }
  const raw =
    meta.date_of_issue ??
    meta.issue_date ??
    meta.dateOfIssue ??
    meta.purchase_date ??
    meta.installation_date ??
    meta.commissioned_at ??
    (typeof meta.installation === "object" && meta.installation !== null && !Array.isArray(meta.installation)
      ? (meta.installation as JsonObject).date
      : undefined);
  if (raw == null) {
    return null;
  }
  if (typeof raw === "number" && Number.isFinite(raw)) {
    const d = new Date(raw);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  if (typeof raw === "string") {
    const d = new Date(raw);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  return null;
}

/**
 * Stable pseudo-coordinates inside India for devices without metadata — map remains usable; UI should mark as approximate.
 */
export function approximateIndiaCoords(deviceId: string): { lat: number; lng: number } {
  let h = 0;
  for (let i = 0; i < deviceId.length; i++) {
    h = (h * 31 + deviceId.charCodeAt(i)) | 0;
  }
  const u1 = (Math.abs(h) % 10000) / 10000;
  const u2 = (Math.abs(h >>> 8) % 10000) / 10000;
  const lat = INDIA_BOUNDS.south + u1 * (INDIA_BOUNDS.north - INDIA_BOUNDS.south);
  const lng = INDIA_BOUNDS.west + u2 * (INDIA_BOUNDS.east - INDIA_BOUNDS.west);
  return { lat, lng };
}

/** Parse warranty end from metadata (ISO date string or epoch ms). */
export function readWarrantyEnd(meta: JsonObject | null | undefined): Date | null {
  if (!meta) {
    return null;
  }
  const raw =
    meta.warranty_end ??
    meta.warranty_expires_at ??
    meta.warrantyEnd ??
    (typeof meta.warranty === "object" && meta.warranty !== null && !Array.isArray(meta.warranty)
      ? (meta.warranty as JsonObject).end
      : undefined);
  if (raw == null) {
    return null;
  }
  if (typeof raw === "number" && Number.isFinite(raw)) {
    const d = new Date(raw);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  if (typeof raw === "string") {
    const d = new Date(raw);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  return null;
}

export function daysUntil(date: Date, now: number): number {
  return Math.ceil((date.getTime() - now) / 86_400_000);
}

export type WarrantyBucket = "expired" | "d30" | "d90" | "ok" | "unknown";

export function warrantyBucket(end: Date | null, now: number): WarrantyBucket {
  if (!end) {
    return "unknown";
  }
  const d = daysUntil(end, now);
  if (d < 0) {
    return "expired";
  }
  if (d <= 30) {
    return "d30";
  }
  if (d <= 90) {
    return "d90";
  }
  return "ok";
}

export function computeFleetHealth(
  lastSeenIso: string | null | undefined,
  displayStatus: "online" | "offline" | "unknown",
): FleetHealth {
  if (displayStatus === "offline") {
    return "offline";
  }
  if (!lastSeenIso) {
    return "unknown";
  }
  const t = new Date(lastSeenIso).getTime();
  if (Number.isNaN(t)) {
    return "unknown";
  }
  const age = Date.now() - t;
  if (age > HEALTH_SLA_STALE_MS) {
    return "offline";
  }
  if (age > HEALTH_SLA_FRESH_MS) {
    return "degraded";
  }
  return "healthy";
}

export function fleetHealthLabel(h: FleetHealth): string {
  switch (h) {
    case "healthy":
      return "Healthy";
    case "degraded":
      return "Degraded";
    case "offline":
      return "Offline";
    default:
      return "Unknown";
  }
}

export function fleetHealthDotClass(h: FleetHealth): string {
  switch (h) {
    case "healthy":
      return "bg-emerald-500";
    case "degraded":
      return "bg-amber-500";
    case "offline":
      return "bg-slate-400 dark:bg-slate-500";
    default:
      return "bg-violet-500";
  }
}
